import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getPricingPlans,
  updatePricingPlan,
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../utils/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

const emptyCoupon = {
  code: '',
  discountType: 'percent',
  discountValue: '',
  appliesToPlan: '',
  maxUses: '',
  expiresAt: '',
};

function PlanCard({ plan, onSave, saving }) {
  const [form, setForm] = useState({
    monthlyPrice: plan.monthly_price,
    yearlyPrice: plan.yearly_price,
    clientLimit: plan.client_limit ?? '',
    reportLimit: plan.report_limit ?? '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = () => {
    onSave(plan.plan_key, {
      monthlyPrice: Number(form.monthlyPrice) || 0,
      yearlyPrice: Number(form.yearlyPrice) || 0,
      clientLimit: form.clientLimit === '' ? null : Number(form.clientLimit),
      reportLimit: form.reportLimit === '' ? null : Number(form.reportLimit),
    });
  };

  const isPayPerReport = plan.plan_key === 'payperuse';

  return (
    <div className="card card-pad">
      <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 14 }}>{plan.display_name}</div>

      <div className="form-group">
        <label className="form-label">{isPayPerReport ? 'Price per Report (₹)' : 'Monthly Price (₹)'}</label>
        <input className="form-input" type="number" value={form.monthlyPrice} onChange={set('monthlyPrice')} />
        {isPayPerReport && (
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
            Charged once per report generated (via Razorpay) — not a recurring monthly fee. This is the
            same price shown on the landing page, docs, and the in-app "Generate Report" button.
          </div>
        )}
      </div>

      {!isPayPerReport && (
      <div className="form-group">
        <label className="form-label">Yearly Price (₹)</label>
        <input className="form-input" type="number" value={form.yearlyPrice} onChange={set('yearlyPrice')} />
      </div>
      )}

      <div className="form-group">
        <label className="form-label">Client Limit (blank = unlimited)</label>
        <input className="form-input" type="number" value={form.clientLimit} onChange={set('clientLimit')} placeholder="Unlimited" />
      </div>

      <div className="form-group">
        <label className="form-label">Report Limit / month (blank = unlimited)</label>
        <input className="form-input" type="number" value={form.reportLimit} onChange={set('reportLimit')} placeholder="Unlimited" />
      </div>

      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={saving} onClick={handleSave}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

export default function SuperAdminPricing() {
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState(null);
  const [couponForm, setCouponForm] = useState(emptyCoupon);
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  const load = async () => {
    try {
      const [plansData, couponsData] = await Promise.all([getPricingPlans(), getCoupons()]);
      setPlans(plansData);
      setCoupons(couponsData);
    } catch {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSavePlan = async (planKey, data) => {
    try {
      setSavingPlan(planKey);
      await updatePricingPlan(planKey, data);
      toast.success('Plan updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update plan');
    } finally {
      setSavingPlan(null);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code || !couponForm.discountValue) {
      toast.error('Coupon code and discount value are required');
      return;
    }

    try {
      setCreatingCoupon(true);
      await createCoupon({
        code: couponForm.code,
        discountType: couponForm.discountType,
        discountValue: Number(couponForm.discountValue),
        appliesToPlan: couponForm.appliesToPlan || null,
        maxUses: couponForm.maxUses ? Number(couponForm.maxUses) : null,
        expiresAt: couponForm.expiresAt || null,
      });
      toast.success('Coupon created');
      setCouponForm(emptyCoupon);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create coupon');
    } finally {
      setCreatingCoupon(false);
    }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await updateCoupon(coupon.id, { isActive: !coupon.is_active });
      toast.success(coupon.is_active ? 'Coupon deactivated' : 'Coupon activated');
      load();
    } catch {
      toast.error('Failed to update coupon');
    }
  };

  const removeCoupon = async (coupon) => {
    if (!window.confirm(`Remove coupon "${coupon.code}"?`)) return;
    try {
      await deleteCoupon(coupon.id);
      toast.success('Coupon removed');
      load();
    } catch {
      toast.error('Failed to remove coupon');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div className="page-title">Pricing</div>
        <div className="page-subtitle">
          Change plan prices and limits, and create discount coupons — takes effect immediately, no deploy needed.
        </div>
      </div>

      <div className="grid grid-3" style={{ marginBottom: 28, gap: 16 }}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.plan_key}
            plan={plan}
            onSave={handleSavePlan}
            saving={savingPlan === plan.plan_key}
          />
        ))}
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>Create Coupon</div>
          <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
            Discount codes agencies can apply at checkout.
          </div>
        </div>

        <div className="card-pad">
          <div className="grid grid-3" style={{ gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Code</label>
              <input
                className="form-input"
                placeholder="e.g. LAUNCH50"
                value={couponForm.code}
                onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Discount Type</label>
              <select
                className="form-select"
                value={couponForm.discountType}
                onChange={(e) => setCouponForm((f) => ({ ...f, discountType: e.target.value }))}
              >
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat amount (₹)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                {couponForm.discountType === 'percent' ? 'Discount %' : 'Discount ₹'}
              </label>
              <input
                className="form-input"
                type="number"
                value={couponForm.discountValue}
                onChange={(e) => setCouponForm((f) => ({ ...f, discountValue: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Applies To Plan (blank = any)</label>
              <select
                className="form-select"
                value={couponForm.appliesToPlan}
                onChange={(e) => setCouponForm((f) => ({ ...f, appliesToPlan: e.target.value }))}
              >
                <option value="">Any plan</option>
                {plans.map((p) => (
                  <option key={p.plan_key} value={p.plan_key}>{p.display_name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Max Uses (blank = unlimited)</label>
              <input
                className="form-input"
                type="number"
                placeholder="Unlimited"
                value={couponForm.maxUses}
                onChange={(e) => setCouponForm((f) => ({ ...f, maxUses: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Expires (blank = never)</label>
              <input
                className="form-input"
                type="date"
                value={couponForm.expiresAt}
                onChange={(e) => setCouponForm((f) => ({ ...f, expiresAt: e.target.value }))}
              />
            </div>
          </div>

          <button className="btn btn-primary" disabled={creatingCoupon} onClick={handleCreateCoupon}>
            {creatingCoupon ? 'Creating...' : 'Create Coupon'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>Active Coupons</div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Plan</th>
                <th>Uses</th>
                <th>Expires</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {coupons.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 24 }}>
                    No coupons yet
                  </td>
                </tr>
              )}
              {coupons.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 800 }}>{c.code}</td>
                  <td>{c.discount_type === 'percent' ? `${fmt(c.discount_value)}%` : `₹${fmt(c.discount_value)}`}</td>
                  <td style={{ textTransform: 'capitalize' }}>{c.applies_to_plan || 'Any'}</td>
                  <td>{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                  <td>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                        background: c.is_active ? 'var(--success-light)' : 'var(--danger-light)',
                        color: c.is_active ? 'var(--success)' : 'var(--danger)',
                      }}
                    >
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => toggleCoupon(c)}>
                      {c.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => removeCoupon(c)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
